// src/App.jsx - Professional Nexcom SPA
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, createContext, useContext } from "react";

import { 
  FaPhone, 
  FaWhatsapp, 
  FaFacebook, 
  FaTwitter, 
  FaInstagram,
  FaBars,
  FaTimes,
  FaHome,
  FaWifi,
  FaBuilding,
  FaEnvelope,
  FaArrowRight,
  FaCheckCircle,
  FaHeadset,
  FaAward,
  FaStar,
  FaArrowUp,
  FaSmile,
  FaTachometerAlt,
  FaShoppingCart,
  FaMapMarkerAlt,
  FaRocket,
  FaHouseUser,
  FaStore,
  FaUser,
  FaUserPlus,
  FaGlobe,
  FaChevronDown,
  FaSpinner,
  FaClock,
  FaInfinity,
  FaGift,
  FaExternalLinkAlt,
  FaServer,
  FaNetworkWired,
  FaMicrochip,
  FaSignal
} from "react-icons/fa";

import "./index.css";

// ─── Import Logo ───
import nexcomLogo from "./assets/nexcom-logo.png";
// ─── Import WiFi Icon Image ───
import wifiIconImage from "./assets/wifilogo.png";

// ─── Import Email Service ───
import { sendEmail } from "./services/emailService";

// ─── Language Context ───
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Return default values instead of throwing error
    return {
      language: 'en',
      toggleLanguage: () => {},
      t: (key) => {
        // Return the key itself if no translation found
        return translations.en[key] || key;
      },
    };
  }
  return context;
};

// ─── Translations ───
const translations = {
  en: {
    // Navigation
    'nav.internet': 'Internet',
    'nav.wifi': 'WiFi',
    'nav.devices': 'Devices',
    'nav.contact': 'Contact',
    'nav.getStarted': 'Get Started',
    'nav.login': 'Login',
    'nav.signup': 'Signup',
    
    // Hero
    'hero.title': 'High-Speed Internet',
    'hero.subtitle': 'Where You Need It',
    'hero.description': 'Reliable, unlimited internet for work, home and everyday life.',
    'hero.checkPlans': 'Check Plans',
    'hero.checkCoverage': 'Check Coverage',
    'hero.badge': 'NEXCOM NETWORK',
    
    // Service Tabs
    'tab.internet.label': 'UNLIMITED',
    'tab.internet.title': 'Internet',
    'tab.internet.desc': 'Reliable internet from KES 2,000/mo',
    'tab.wifi.label': 'On Demand',
    'tab.wifi.title': 'WiFi',
    'tab.wifi.desc': 'WiFi passes — no subscription needed',
    'tab.devices.label': 'SHOP',
    'tab.devices.title': 'Devices',
    'tab.devices.desc': 'Everything you need to get connected',
    
    // Internet Section
    'internet.eyebrow': '// Internet',
    'internet.title': 'Internet Plans',
    'internet.subtitle': 'Unlimited internet with simple monthly plans. No data caps. No peak-hour throttling.',
    'internet.orderNow': 'Order Now',
    'internet.mostPopular': 'Most Popular',
    'internet.perMonth': '/month',
    
    // WiFi Section
    'wifi.eyebrow': '// On-Demand WiFi',
    'wifi.title': 'WiFi Passes',
    'wifi.subtitle': 'No subscription. Choose a WiFi pass and get connected.',
    'wifi.pass': 'Pass',
    'wifi.validity': 'Validity',
    'wifi.amount': 'Amount',
    'wifi.buyPass': 'Buy a Pass',
    'wifi.description': 'Choose from our flexible WiFi pass options. Perfect for short-term or occasional use.',
    
    // Business Section
    'business.eyebrow': '// Business',
    'business.title': 'Enterprise Solutions',
    'business.subtitle': 'Dedicated capacity, SLA-backed connectivity, and account management for businesses of all sizes.',
    'business.contactSales': 'Contact Sales',
    'business.letsTalk': "Let's Talk",
    'business.tailored': 'Tailored to your needs',
    'business.feature1': 'Dedicated symmetric bandwidth',
    'business.feature2': 'Multi-site options available',
    'business.feature3': 'Full SLA with financial backing',
    'business.feature4': '24/7 priority support',
    'business.feature5': 'Static IP block + custom routing',
    'business.feature6': 'Guest WiFi infrastructure included',
    
    // Testimonials
    'testimonials.title': 'What Our Clients Say',
    'testimonials.subtitle': 'Real experiences from real customers',
    
    // Coverage
    'coverage.title': 'Are You in Our Coverage Area?',
    'coverage.desc': 'We run on fixed wireless — coverage depends on your location in Kisumu.',
    'coverage.check': 'Check My Area',
    'coverage.call': 'Call Us Now',
    
    // Footer
    'footer.brandText': 'Fixed wireless internet for homes and businesses in Kisumu. Built on over 20 years of network operations across East Africa. CA Licensed NFP Tier 2 ISP.',
    'footer.services': 'Services',
    'footer.support': 'Support',
    'footer.about': 'About Us',
    'footer.legal': 'Legal',
    'footer.faqs': 'FAQs',
    'footer.contactUs': 'Contact Us',
    'footer.requestCallback': 'Request Callback',
    'footer.aboutUs': 'About Us',
    'footer.coverageMap': 'Coverage Map',
    'footer.careers': 'Careers',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms & Conditions',
    'footer.cookies': 'Cookie Policy',
    'footer.rights': 'All Rights Reserved.',
    
    // Modal
    'modal.title': 'Get Connected',
    'modal.name': 'Full Name *',
    'modal.email': 'Email Address',
    'modal.phone': 'Phone Number *',
    'modal.message': 'Tell us what you need...',
    'modal.send': 'Send Message',
    'modal.sending': 'Sending...',
    'modal.success': 'Message sent successfully! We\'ll contact you shortly.',
    
    // Language
    'lang.english': 'English',
    'lang.swahili': 'Kiswahili',
  },
  sw: {
    // Navigation
    'nav.internet': 'Intaneti',
    'nav.wifi': 'WiFi',
    'nav.devices': 'Vifaa',
    'nav.contact': 'Wasiliana',
    'nav.getStarted': 'Anza',
    'nav.login': 'Ingia',
    'nav.signup': 'Jisajili',
    
    // Hero
    'hero.title': 'Intaneti ya Kasi',
    'hero.subtitle': 'Mahali Unapohitaji',
    'hero.description': 'Intaneti ya kuaminika, isiyo na kikomo kwa kazi, nyumbani na maisha ya kila siku.',
    'hero.checkPlans': 'Angalia Mpango',
    'hero.checkCoverage': 'Angalia Ufikiaji',
    'hero.badge': 'MTANDAO WA NEXCOM',
    
    // Service Tabs
    'tab.internet.label': 'BILA KIKOMO',
    'tab.internet.title': 'Intaneti',
    'tab.internet.desc': 'Intaneti ya kuaminika kutoka KES 2,000/mwezi',
    'tab.wifi.label': 'Kwa Mahitaji',
    'tab.wifi.title': 'WiFi',
    'tab.wifi.desc': 'Vibali vya WiFi — hakuna usajili unaohitajika',
    'tab.devices.label': 'DUKA',
    'tab.devices.title': 'Vifaa',
    'tab.devices.desc': 'Kila kitu unachohitaji kuunganishwa',
    
    // Internet Section
    'internet.eyebrow': '// Intaneti',
    'internet.title': 'Mipango ya Intaneti',
    'internet.subtitle': 'Intaneti isiyo na kikomo na mipango rahisi ya kila mwezi. Hakuna kikomo cha data. Hakuna kupunguza kasi wakati wa mwisho wa mwezi.',
    'internet.orderNow': 'Amri Sasa',
    'internet.mostPopular': 'Inajulikana Sana',
    'internet.perMonth': '/mwezi',
    
    // WiFi Section
    'wifi.eyebrow': '// WiFi kwa Mahitaji',
    'wifi.title': 'Vibali vya WiFi',
    'wifi.subtitle': 'Hakuna usajili. Chagua kibali cha WiFi na uunganishwe.',
    'wifi.pass': 'Kibali',
    'wifi.validity': 'Muda',
    'wifi.amount': 'Kiasi',
    'wifi.buyPass': 'Nunua Kibali',
    'wifi.description': 'Chagua kutoka kwa chaguzi zetu za vibali vya WiFi. Inafaa kwa matumizi ya muda mfupi au ya mara kwa mara.',
    
    // Business Section
    'business.eyebrow': '// Biashara',
    'business.title': 'Suluhisho za Biashara',
    'business.subtitle': 'Uwezo maalum, muunganisho wa SLA, na usimamizi wa akaunti kwa biashara za ukubwa wote.',
    'business.contactSales': 'Wasiliana na Mauzo',
    'business.letsTalk': 'Tuzungumze',
    'business.tailored': 'Imebadilishwa kwa mahitaji yako',
    'business.feature1': 'Kipimo maalum cha mtandao',
    'business.feature2': 'Chaguzi za tovuti nyingi zinapatikana',
    'business.feature3': 'SLA kamili na udhamini wa kifedha',
    'business.feature4': 'Msaada wa kipaumbele 24/7',
    'business.feature5': 'Kizuizi cha IP tuli + uelekezaji maalum',
    'business.feature6': 'Miundombinu ya WiFi ya wageni imejumuishwa',
    
    // Testimonials
    'testimonials.title': 'Wateja Wetu Wanasema',
    'testimonials.subtitle': 'Uzoefu halisi kutoka kwa wateja halisi',
    
    // Coverage
    'coverage.title': 'Je, Uko Katika Eneo Letu?',
    'coverage.desc': 'Tunatumia mtandao wa waya usio na waya — ufikiaji unategemea eneo lako Kisumu.',
    'coverage.check': 'Angalia Eneo Langu',
    'coverage.call': 'Tupigie Sasa',
    
    // Footer
    'footer.brandText': 'Intaneti ya waya isiyo na waya kwa nyumba na biashara Kisumu. Imejengwa juu ya zaidi ya miaka 20 ya uendeshaji wa mtandao Afrika Mashariki. CA Licensed NFP Tier 2 ISP.',
    'footer.services': 'Huduma',
    'footer.support': 'Msaada',
    'footer.about': 'Kuhusu Sisi',
    'footer.legal': 'Sheria',
    'footer.faqs': 'Maswali',
    'footer.contactUs': 'Wasiliana Nasi',
    'footer.requestCallback': 'Omba Mrejesho',
    'footer.aboutUs': 'Kuhusu Sisi',
    'footer.coverageMap': 'Ramani ya Ufikiaji',
    'footer.careers': 'Kazi',
    'footer.privacy': 'Sera ya Faragha',
    'footer.terms': 'Masharti na Mashart',
    'footer.cookies': 'Sera ya Kuki',
    'footer.rights': 'Haki Zote Zimehifadhiwa.',
    
    // Modal
    'modal.title': 'Unganishwa',
    'modal.name': 'Jina Kamili *',
    'modal.email': 'Barua Pepe',
    'modal.phone': 'Nambari ya Simu *',
    'modal.message': 'Tuambie unachohitaji...',
    'modal.send': 'Tuma Ujumbe',
    'modal.sending': 'Inatuma...',
    'modal.success': 'Ujumbe umetumwa kwa mafanikio! Tutawasiliana nawe hivi karibuni.',
    
    // Language
    'lang.english': 'Kiingereza',
    'lang.swahili': 'Kiswahili',
  }
};

// ─── Language Provider ───
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('nexcom-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('nexcom-language', language);
  }, [language]);

  const toggleLanguage = (lang) => {
    setLanguage(lang);
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// ─── Constants ───
const NEXCOM_STORE_URL = "https://nexcom.onelynq.app/";
const ONELYNQ_APP_URL = "https://nexcom.onelynq.app/";

// ─── Bubble Component ───
const Bubble = ({ size, x, y, duration, delay, direction }) => {
  const getAnimation = () => {
    const moveAmount = 60 + Math.random() * 40;
    const randomOffset = () => Math.random() * 50 - 25;
    switch(direction) {
      case 'up': return { y: [0, -moveAmount, 0], x: [0, randomOffset(), 0] };
      case 'down': return { y: [0, moveAmount, 0], x: [0, randomOffset(), 0] };
      case 'left': return { x: [0, -moveAmount, 0], y: [0, randomOffset(), 0] };
      case 'right': return { x: [0, moveAmount, 0], y: [0, randomOffset(), 0] };
      default: return { y: [0, -moveAmount, 0], x: [0, randomOffset(), 0] };
    }
  };

  return (
    <motion.div
      className="bubble"
      style={{ width: size, height: size, left: x, top: y }}
      animate={getAnimation()}
      transition={{ duration, repeat: Infinity, ease: "easeInOut", delay }}
    />
  );
};

// ─── Particle Component ───
const Particle = ({ size, x, y, duration, delay }) => (
  <motion.div
    className="particle"
    style={{ width: size, height: size, left: x, top: y }}
    animate={{
      y: [0, -30 - Math.random() * 30, 0],
      x: [0, Math.random() * 20 - 10, 0],
      opacity: [0, 0.6, 0],
      scale: [0.5, 1.2, 0.5],
    }}
    transition={{ duration, repeat: Infinity, ease: "easeInOut", delay }}
  />
);

function AppContent() {
  const { t, language, toggleLanguage } = useLanguage();
  
  // ─── State ───
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [activeSection, setActiveSection] = useState("internet");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // ─── Dynamic Year ───
  const currentYear = new Date().getFullYear();

  // ─── Refs ───
  const sectionRefs = {
    home: useRef(null),
    internet: useRef(null),
    business: useRef(null),
    wifi: useRef(null),
    devices: useRef(null),
    coverage: useRef(null),
    contact: useRef(null),
  };

  const dropdownRef = useRef(null);
  const langDropdownRef = useRef(null);

  // ─── Scroll Handler ───
  useEffect(() => {
    const handleScroll = () => {
      const header = document.getElementById('header');
      if (header) setIsSticky(window.pageYOffset > header.offsetTop);
      setShowScrollTop(window.pageYOffset > 400);

      const scrollPosition = window.scrollY + 200;
      Object.entries(sectionRefs).forEach(([key, ref]) => {
        if (ref.current) {
          const { offsetTop, offsetHeight } = ref.current;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(key);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ─── Click Outside Handler ───
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Scroll to Section ───
  const scrollToSection = (sectionId) => {
    const section = sectionRefs[sectionId];
    if (section?.current) {
      const headerOffset = 80;
      const elementPosition = section.current.offsetTop;
      window.scrollTo({
        top: elementPosition - headerOffset,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
      setIsMenuOpen(false);
    }
  };

  // ─── Form Handlers ───
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError(null);
    if (formSuccess) setFormSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setFormError("Please enter your full name");
      return;
    }
    if (!formData.phone.trim()) {
      setFormError("Please enter your phone number");
      return;
    }
    if (!formData.message.trim()) {
      setFormError("Please enter a message");
      return;
    }

    setIsLoading(true);
    setFormError(null);
    setFormSuccess(false);

    try {
      const result = await sendEmail(formData);
      
      if (result.success) {
        setFormSuccess(true);
        setFormData({ name: "", email: "", phone: "", message: "" });
        
        setTimeout(() => {
          setIsModalOpen(false);
          setFormSuccess(false);
        }, 2000);
      } else {
        setFormError(result.error || "Failed to send message. Please try again.");
      }
    } catch (error) {
      setFormError("An unexpected error occurred. Please try again.");
      console.error("Form submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // ─── Navigate to Store ───
  const navigateToStore = () => {
    window.open(NEXCOM_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  // ─── Check Coverage ───
  const checkCoverage = () => {
    window.open('https://nexcom.onelynq.app/', '_blank', 'noopener,noreferrer');
  };

  // ─── Dropdown Links ───
  const dropdownLinks = [
    { label: t('nav.login'), url: "https://nexcom.onelynq.app/login", icon: FaUser },
    { label: t('nav.signup'), url: "https://nexcom.onelynq.app/signup", icon: FaUserPlus },
  ];

  // ─── Language Options ───
  const languageOptions = [
    { code: 'en', label: 'English' },
    { code: 'sw', label: 'Kiswahili' },
  ];

  // ─── Data ───
  const internetPlans = [
    {
      name: "Nexcom Lite",
      speed: "10 Mbps",
      price: "2,000",
      features: [
        "Unlimited data — no caps",
        "2–4 simultaneous devices",
        "Streaming & browsing",
        "Video calls & WhatsApp",
        "WhatsApp & phone support"
      ],
      popular: false
    },
    {
      name: "Nexcom Plus",
      speed: "30 Mbps",
      price: "3,000",
      features: [
        "Unlimited data — no caps",
        "5–8 simultaneous devices",
        "4K streaming & gaming",
        "Online gaming & cloud",
        "Priority phone & WhatsApp"
      ],
      popular: true
    },
    {
      name: "Nexcom Premium",
      speed: "50 Mbps",
      price: "5,500",
      features: [
        "Unlimited data — no caps",
        "10+ simultaneous devices",
        "Ultra HD streaming & gaming",
        "Professional cloud work",
        "24/7 priority support"
      ],
      popular: false
    }
  ];

  const wifiBundles = [
    { pass: "Hourly", validity: "1 hour", amount: "20" },
    { pass: "Daily", validity: "24 hours", amount: "80" },
    { pass: "Weekly", validity: "7 Days", amount: "400" }
  ];

  const locations = [
    { name: "Milimani", icon: FaMapMarkerAlt },
    { name: "Kondele", icon: FaMapMarkerAlt },
    { name: "Mamboleo", icon: FaMapMarkerAlt },
    { name: "Riat", icon: FaMapMarkerAlt },
    { name: "Nyalenda", icon: FaMapMarkerAlt }
  ];

  const testimonials = [
    {
      name: "John Mwangi",
      location: "Kisumu",
      text: "Nexcom has transformed our home internet experience. Fast, reliable, and excellent customer service!",
      rating: 5
    },
    {
      name: "Mary Wanjiru",
      location: "Kisumu",
      text: "The business internet package has been a game-changer for our company. Highly recommended!",
      rating: 5
    },
    {
      name: "Peter Ochieng",
      location: "Kisumu",
      text: "Great value for money. The installation was quick and the support team is very responsive.",
      rating: 4
    },
  ];

  const quickLinks = {
    findUs: [
      { name: "Facebook", link: "https://www.facebook.com/NexcomKe", icon: FaFacebook },
      { name: "Twitter", link: "https://twitter.com/NexcomKe?s=20", icon: FaTwitter },
      { name: "Instagram", link: "https://instagram.com/NexcomKe", icon: FaInstagram },
      { name: "Whatsapp", link: "https://api.whatsapp.com/send?phone=254730320320", icon: FaWhatsapp },
    ],
  };

  const bubbles = [
    { size: 120, x: '3%', y: '5%', duration: 14, delay: 0, direction: 'up' },
    { size: 80, x: '12%', y: '65%', duration: 10, delay: 1, direction: 'down' },
    { size: 180, x: '80%', y: '8%', duration: 16, delay: 0.5, direction: 'left' },
    { size: 100, x: '72%', y: '70%', duration: 11, delay: 0.5, direction: 'right' },
    { size: 60, x: '40%', y: '5%', duration: 9, delay: 1.5, direction: 'up' },
    { size: 140, x: '25%', y: '75%', duration: 13, delay: 3, direction: 'down' },
    { size: 70, x: '55%', y: '35%', duration: 10, delay: 2.5, direction: 'left' },
    { size: 85, x: '88%', y: '45%', duration: 12, delay: 4, direction: 'up' },
    { size: 45, x: '8%', y: '40%', duration: 8, delay: 3.5, direction: 'right' },
    { size: 75, x: '48%', y: '55%', duration: 11, delay: 1.8, direction: 'down' },
    { size: 40, x: '65%', y: '8%', duration: 7, delay: 4.5, direction: 'left' },
    { size: 95, x: '18%', y: '20%', duration: 15, delay: 0.8, direction: 'right' },
  ];

  const particles = [
    { size: 4, x: '10%', y: '20%', duration: 5, delay: 0 },
    { size: 3, x: '30%', y: '50%', duration: 6, delay: 1 },
    { size: 5, x: '60%', y: '15%', duration: 4.5, delay: 0.5 },
    { size: 3, x: '80%', y: '60%', duration: 5.5, delay: 2 },
    { size: 4, x: '45%', y: '70%', duration: 6.5, delay: 1.5 },
    { size: 3, x: '70%', y: '40%', duration: 5, delay: 3 },
    { size: 5, x: '20%', y: '35%', duration: 4, delay: 2.5 },
    { size: 4, x: '90%', y: '25%', duration: 5.5, delay: 4 },
  ];

  return (
    <div className="app">
      {/* ─── HEADER ─── */}
      <header className="header" id="header-area">
        <div className={`main-nav ${isSticky ? 'sticky' : ''}`} id="header">
          <div className="main-nav-inner">
            <button onClick={() => scrollToSection('home')} className="logo">
              <img src={nexcomLogo} alt="Nexcom Internet Services" className="logo-image" />
            </button>

            <nav className="desktop-nav">
              <button
                onClick={() => scrollToSection('internet')}
                className={`nav-link ${activeSection === 'internet' ? 'active' : ''}`}
              >
                <span>{t('nav.internet')}</span>
              </button>
              
              <button
                onClick={() => scrollToSection('wifi')}
                className={`nav-link ${activeSection === 'wifi' ? 'active' : ''}`}
              >
                <span>{t('nav.wifi')}</span>
              </button>
              
              <Link to="/devices" className="nav-link">
                <span>{t('nav.devices')}</span>
              </Link>
            </nav>

            <div className="nav-right">
              {/* Dropdown / Get Started Button */}
              <div className="dropdown-wrapper" ref={dropdownRef}>
                <button 
                  className={`nav-get-started ${isDropdownOpen ? 'open' : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {t('nav.getStarted')} <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`} />
                </button>
                
                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    {dropdownLinks.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <a
                          key={index}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dropdown-item"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <Icon className="dropdown-item-icon" />
                          <span>{item.label}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Language Dropdown - Now after Get Started */}
              <div className="lang-dropdown-wrapper" ref={langDropdownRef}>
                <button 
                  className={`lang-dropdown-btn ${isLangDropdownOpen ? 'open' : ''}`}
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                >
                  <FaGlobe className="lang-icon" />
                  <span className="lang-current-label">
                    {language === 'en' ? 'EN' : 'SW'}
                  </span>
                  <FaChevronDown className={`lang-dropdown-arrow ${isLangDropdownOpen ? 'rotated' : ''}`} />
                </button>
                
                {isLangDropdownOpen && (
                  <div className="lang-dropdown-menu">
                    {languageOptions.map((option) => (
                      <button
                        key={option.code}
                        className={`lang-dropdown-item ${language === option.code ? 'active' : ''}`}
                        onClick={() => {
                          toggleLanguage(option.code);
                          setIsLangDropdownOpen(false);
                        }}
                      >
                        <span className="lang-option-label">{option.label}</span>
                        {language === option.code && (
                          <span className="lang-check">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-toggle"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </header>

      {/* ─── MOBILE MENU ─── */}
      {isMenuOpen && (
        <div className="mobile-overlay" onClick={() => setIsMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <button onClick={() => setIsMenuOpen(false)} className="mobile-menu-close">
                <FaTimes />
              </button>
            </div>
            <div className="mobile-menu-links">
              <button
                onClick={() => {
                  scrollToSection('internet');
                  setIsMenuOpen(false);
                }}
                className={`mobile-link ${activeSection === 'internet' ? 'active' : ''}`}
              >
                <FaHouseUser className="mobile-link-icon" />
                <span>{t('nav.internet')}</span>
              </button>
              
              <button
                onClick={() => {
                  scrollToSection('wifi');
                  setIsMenuOpen(false);
                }}
                className={`mobile-link ${activeSection === 'wifi' ? 'active' : ''}`}
              >
                <FaWifi className="mobile-link-icon" />
                <span>{t('nav.wifi')}</span>
              </button>
              
              <Link to="/devices" className="mobile-link" onClick={() => setIsMenuOpen(false)}>
                <FaServer className="mobile-link-icon" />
                <span>{t('nav.devices')}</span>
              </Link>
              
              <Link to="/contact" className="mobile-link" onClick={() => setIsMenuOpen(false)}>
                <FaEnvelope className="mobile-link-icon" />
                <span>{t('nav.contact')}</span>
              </Link>
              
              <div className="mobile-divider"></div>
              
              {/* Mobile Language Toggle */}
              <div className="mobile-language-section">
                <span className="mobile-language-label">Language / Lugha</span>
                <div className="mobile-language-buttons">
                  {languageOptions.map((option) => (
                    <button 
                      key={option.code}
                      className={`mobile-lang-btn ${language === option.code ? 'active' : ''}`}
                      onClick={() => {
                        toggleLanguage(option.code);
                        setIsMenuOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mobile-divider"></div>
              
              <a 
                href="https://nexcom.onelynq.app/login"
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <FaUser className="mobile-link-icon" />
                <span>{t('nav.login')}</span>
              </a>
              <a 
                href="https://nexcom.onelynq.app/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <FaUserPlus className="mobile-link-icon" />
                <span>{t('nav.signup')}</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ─── HERO ─── */}
      <section ref={sectionRefs.home} id="home" className="hero">
        <div className="hero-bg">
          {bubbles.map((bubble, i) => <Bubble key={`b-${i}`} {...bubble} />)}
          {particles.map((particle, i) => <Particle key={`p-${i}`} {...particle} />)}
        </div>

        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            {t('hero.badge')}
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t('hero.title')} <br />
            <span className="highlight" style={{ 
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: '#ffffff',
              background: 'none',
              WebkitBackgroundClip: 'unset',
              WebkitTextFillColor: 'unset'
            }}>
              {t('hero.subtitle')}
            </span>
          </motion.h1>

          <motion.p            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t('hero.description')}
          </motion.p>
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button onClick={() => scrollToSection('internet')} className="btn-primary">
              <FaRocket /> {t('hero.checkPlans')}
            </button>
            <button onClick={checkCoverage} className="btn-secondary">
              <FaMapMarkerAlt /> {t('hero.checkCoverage')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* ─── SERVICE TABS ─── */}
      <div className="service-select">
        <div className="service-tabs">
          <button onClick={() => scrollToSection('internet')} className="service-tab">
            <span className="tab-label">{t('tab.internet.label')}</span>
            <span className="tab-title">{t('tab.internet.title')}</span>
            <span className="tab-desc">{t('tab.internet.desc')}</span>
          </button>

          <button onClick={() => scrollToSection('wifi')} className="service-tab">
            <span className="tab-label">{t('tab.wifi.label')}</span>
            <span className="tab-title">{t('tab.wifi.title')}</span>
            <span className="tab-desc">{t('tab.wifi.desc')}</span>
          </button>

          <Link to="/devices" className="service-tab" style={{ textDecoration: 'none' }}>
            <span className="tab-label">{t('tab.devices.label')}</span>
            <span className="tab-title">{t('tab.devices.title')}</span>
            <span className="tab-desc">{t('tab.devices.desc')}</span>
          </Link>
        </div>
      </div>

      {/* ─── INTERNET ─── */}
      <section ref={sectionRefs.internet} id="internet" className="section section-white">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">{t('internet.eyebrow')}</span>
            <h2 className="section-title">
            {t('internet.title')}<br />
            </h2>
            <p className="section-sub">
            {t('internet.subtitle')}
            </p>
          </motion.div>

          <div className="plans-grid">
            {internetPlans.map((plan, i) => (
              <motion.div
                key={i}
                className={`plan-card ${plan.popular ? 'featured' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                {plan.popular && <span className="plan-badge">{t('internet.mostPopular')}</span>}
                <div className="plan-name">{plan.name}</div>
                <div className="plan-speed">
                  <span className="speed-number">{plan.speed.replace(' Mbps', '')}</span>
                  <span className="speed-unit">Mbps</span>
                </div>
                <div className="plan-price-row">
                  <span className="plan-price-curr">KES</span>
                  <span className="plan-price">{plan.price}</span>
                  <span className="plan-price-period">{t('internet.perMonth')}</span>
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, j) => (
                    <li key={j}>
                      <span className="feat-check">
                        <svg viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#14b8a6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button onClick={navigateToStore} className={plan.popular ? "btn-primary" : "btn-secondary-outline"}>
                  {t('internet.orderNow')}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WIFI ─── */}
      <section ref={sectionRefs.wifi} id="wifi" className="section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">{t('wifi.eyebrow')}</span>
            <h2 className="section-title">
            {t('wifi.title')}<br />
            </h2>
            <p className="section-sub">
            {t('wifi.subtitle')}
            </p>
          </motion.div>

          <div className="wifi-full-width">
            <motion.div
              className="wifi-card-full"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="wifi-card-header">
                <span className="wifi-icon home" style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  background: 'rgba(249, 115, 22, 0.12)',
                  color: '#f97316',
                  overflow: 'hidden',
                }}>
                  <img 
                    src={wifiIconImage} 
                    alt="WiFi" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </span>
                <h3>{t('wifi.title')}</h3>
              </div>
              <p className="wifi-desc">
                {t('wifi.description')}
              </p>
              <table className="bundle-table wifi-pass-table">
                <thead>
                  <tr>
                    <th>{t('wifi.pass')}</th>
                    <th>{t('wifi.validity')}</th>
                    <th>{t('wifi.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {wifiBundles.map((bundle, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{bundle.pass}</strong>
                      </td>
                      <td>{bundle.validity}</td>
                      <td>KES {bundle.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn-primary" onClick={navigateToStore}>
                <FaShoppingCart /> {t('wifi.buyPass')}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── BUSINESS ─── */}
      <section ref={sectionRefs.business} id="business" className="section section-surface">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">{t('business.eyebrow')}</span>
            <h2 className="section-title">
            {t('business.title')}<br />
            </h2>
            <p className="section-sub">
              {t('business.subtitle')}
            </p>
          </motion.div>

          <div className="biz-enterprise-wrapper">
            <motion.div
              className="biz-enterprise-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="enterprise-grid">
                <div className="enterprise-left">
                  <div className="enterprise-badge">
                    <FaBuilding className="enterprise-badge-icon" />
                    <span>Enterprise</span>
                  </div>
                  <div className="enterprise-speed">
                    <span className="speed-number custom-speed">Custom</span>
                    <span className="speed-label">Bandwidth</span>
                  </div>
                  <div className="enterprise-price-row">
                    <span className="enterprise-price">{t('business.letsTalk')}</span>
                    <span className="enterprise-price-sub">{t('business.tailored')}</span>
                  </div>
                </div>
                <div className="enterprise-right">
                  <ul className="enterprise-features">
                    <li>
                      <span className="feat-check">
                        <svg viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#99f6e4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {t('business.feature1')}
                    </li>
                    <li>
                      <span className="feat-check">
                        <svg viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#99f6e4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {t('business.feature2')}
                    </li>
                    <li>
                      <span className="feat-check">
                        <svg viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#99f6e4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {t('business.feature3')}
                    </li>
                    <li>
                      <span className="feat-check">
                        <svg viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#99f6e4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {t('business.feature4')}
                    </li>
                    <li>
                      <span className="feat-check">
                        <svg viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#99f6e4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {t('business.feature5')}
                    </li>
                    <li>
                      <span className="feat-check">
                        <svg viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#99f6e4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {t('business.feature6')}
                    </li>
                  </ul>
                </div>
              </div>
              <div className="enterprise-actions">
                <button onClick={navigateToStore} className="btn-primary">
                  {t('business.contactSales')} <FaArrowRight />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="section section-alt">
        <div className="container">
          <div className="testimonials-header">
            <h2 className="section-title">
              {t('testimonials.title')}
            </h2>
            <p className="section-sub">{t('testimonials.subtitle')}</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                className="testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <FaStar key={j} className="testimonial-star" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div>
                  <p className="testimonial-name">{testimonial.name}</p>
                  <p className="testimonial-location">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COVERAGE ─── */}
      <section ref={sectionRefs.coverage} id="coverage" className="coverage-section">
        <div className="container">
          <h2 className="coverage-title">{t('coverage.title')}</h2>
          <p className="coverage-desc">
            {t('coverage.desc')}
          </p>

          <div className="coverage-actions">
            <button className="coverage-btn-primary" onClick={checkCoverage}>
              <FaMapMarkerAlt /> {t('coverage.check')}
            </button>
            <a href="tel:0730320000" className="coverage-btn-secondary">
              <FaPhone /> {t('coverage.call')}
            </a>
          </div>

          <div className="coverage-contacts">
            <a href="tel:0730320000"><FaPhone /> 0730 320 000</a>
            <a href="https://wa.me/254111636443"><FaWhatsapp /> WhatsApp</a>
            <a href="mailto:hello@nexcom.ke"><FaEnvelope /> hello@nexcom.ke</a>
          </div>
        </div>
        <div className="locations-grid">
          {locations.map((location, index) => {
            const Icon = location.icon;
            return (
              <motion.div
                key={index}
                className="location-item"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Icon className="location-icon" />
                <span>{location.name}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src={nexcomLogo} alt="Nexcom Internet Services" className="footer-logo-image" />
              </div>
              <p className="footer-brand-text">
                {t('footer.brandText')}
              </p>
              <div className="footer-socials">
                {quickLinks.findUs.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <a key={i} href={item.link} target="_blank" rel="noopener noreferrer" className="footer-social">
                      <Icon />
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="footer-col">
              <h4>{t('footer.services')}</h4>
              <ul>
                <li>
                  <button onClick={() => scrollToSection('internet')} className="footer-link">
                    {t('nav.internet')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('wifi')} className="footer-link">
                    {t('nav.wifi')}
                  </button>
                </li>
                <li>
                  <Link to="/devices" className="footer-link">
                    {t('nav.devices')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t('footer.support')}</h4>
              <ul>
                <li>
                  <Link to="/support" className="footer-link">
                    {t('footer.faqs')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="footer-link">
                    {t('footer.contactUs')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="footer-link">
                    {t('footer.requestCallback')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t('footer.about')}</h4>
              <ul>
                <li>
                  <Link to="/about" className="footer-link">
                    {t('footer.aboutUs')}
                  </Link>
                </li>
                <li>
                  <Link to="/coverage" className="footer-link">
                    {t('footer.coverageMap')}
                  </Link>
                </li>
                <li>
                  <Link to="/careers" className="footer-link">
                    {t('footer.careers')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t('footer.legal')}</h4>
              <ul>
                <li>
                  <Link to="/legal/privacy" className="footer-link">
                    {t('footer.privacy')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/terms" className="footer-link">
                    {t('footer.terms')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/cookies" className="footer-link">
                    {t('footer.cookies')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <span>
              &copy; {currentYear} Nexcom. {t('footer.rights')}
            </span>
            <div className="footer-legal">
            </div>
          </div>
        </div>
      </footer>
          
      {/* ─── SCROLL TO TOP ─── */}
      {showScrollTop && (
        <button onClick={scrollToTop} className="scroll-top visible">
          <FaArrowUp />
        </button>
      )}

      {/* ─── MODAL ─── */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <motion.div 
            className="modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{t('modal.title')}</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {formSuccess && (
                <div className="form-success">
                  <FaCheckCircle /> {t('modal.success')}
                </div>
              )}
              
              {formError && (
                <div className="form-error">
                  ⚠️ {formError}
                </div>
              )}

              <input
                type="text"
                name="name"
                placeholder={t('modal.name')}
                required
                value={formData.name}
                onChange={handleFormChange}
                disabled={isLoading}
              />
              <input
                type="email"
                name="email"
                placeholder={t('modal.email')}
                value={formData.email}
                onChange={handleFormChange}
                disabled={isLoading}
              />
              <input
                type="tel"
                name="phone"
                placeholder={t('modal.phone')}
                required
                value={formData.phone}
                onChange={handleFormChange}
                disabled={isLoading}
              />
              <textarea
                name="message"
                placeholder={t('modal.message')}
                rows="4"
                value={formData.message}
                onChange={handleFormChange}
                disabled={isLoading}
              />
              <button 
                type="submit" 
                className="modal-submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="spinner" /> {t('modal.sending')}
                  </>
                ) : (
                  <>
                    <FaEnvelope /> {t('modal.send')}
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {/* ─── MOBILE BOTTOM NAV ─── */}
      <nav className="mobile-bottom-nav">
        <button
          className={activeSection === 'internet' ? 'active' : ''}
          onClick={() => scrollToSection('internet')}
        >
          <FaHouseUser />
          <span>{t('nav.internet')}</span>
        </button>
        
        <button
          className={activeSection === 'wifi' ? 'active' : ''}
          onClick={() => scrollToSection('wifi')}
        >
          <FaWifi />
          <span>{t('nav.wifi')}</span>
        </button>
        
        <Link to="/devices" className={activeSection === 'devices' ? 'active' : ''} style={{ textDecoration: 'none' }}>
          <FaServer />
          <span>{t('nav.devices')}</span>
        </Link>
        
        <Link to="/contact" className={activeSection === 'contact' ? 'active' : ''} style={{ textDecoration: 'none' }}>
          <FaEnvelope />
          <span>{t('nav.contact')}</span>
        </Link>
      </nav>

      <div className="mobile-spacer"></div>
    </div>
  );
}

// ─── Main App Component ───
export default function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}